// Hand-rolled SVG chart — the Highcharts replacement. It consumes the SAME spec
// objects the shared debtDetailCharts.ts already produces (chart/title/xAxis/
// yAxis/series), so no shared code changes. Pure SVG + DOM (createElementNS),
// styled by attributes + external CSS — zero inline style attributes, so it runs
// under style-src 'self'. Supports the line + column charts the app uses.
import { h, s } from '../core/dom';

interface Series {
	name: string;
	data: (number | null)[];
	color?: string;
	dashStyle?: string;
}
interface PlotLine {
	value: number;
	color?: string;
	label?: { text?: string };
}
export interface ChartSpec {
	chart?: { type?: string };
	title?: { text?: string };
	xAxis?: { categories?: string[] };
	yAxis?: { min?: number; plotLines?: PlotLine[] };
	series?: Series[];
}

const W = 720;
const H = 320;
const PAD = { top: 16, right: 16, bottom: 36, left: 56 };

const dashArray = (d?: string): string | null =>
	d === 'Dash' ? '8 5' : d === 'ShortDash' ? '4 4' : null;

function niceMax(v: number): number {
	if (v <= 0) return 1;
	const pow = Math.pow(10, Math.floor(Math.log10(v)));
	return Math.ceil(v / pow) * pow;
}

export function renderChart(spec: ChartSpec): HTMLElement {
	const type = spec.chart?.type ?? 'line';
	const cats = spec.xAxis?.categories ?? [];
	const series = spec.series ?? [];
	const plotLines = spec.yAxis?.plotLines ?? [];

	const allValues = [
		...series.flatMap((sr) => sr.data.filter((v): v is number => v != null)),
		...plotLines.map((p) => p.value),
	];
	const yMin = spec.yAxis?.min ?? 0;
	const yMax = niceMax(Math.max(yMin + 1, ...(allValues.length ? allValues : [1])));

	const plotW = W - PAD.left - PAD.right;
	const plotH = H - PAD.top - PAD.bottom;
	const xFor = (i: number) =>
		PAD.left + (cats.length <= 1 ? plotW / 2 : (plotW * i) / (cats.length - 1));
	const xBand = (i: number) => PAD.left + (plotW * (i + 0.5)) / Math.max(1, cats.length);
	const yFor = (v: number) => PAD.top + plotH * (1 - (v - yMin) / (yMax - yMin));

	const svg = s('svg', { viewBox: `0 0 ${W} ${H}`, role: 'img', 'aria-label': spec.title?.text ?? 'chart' });

	// Y gridlines + labels (5 ticks).
	for (let t = 0; t <= 4; t++) {
		const v = yMin + ((yMax - yMin) * t) / 4;
		const y = yFor(v);
		svg.append(s('line', { x1: PAD.left, y1: y, x2: W - PAD.right, y2: y, stroke: '#e2e8f0', 'stroke-width': 1 }));
		svg.append(s('text', { x: PAD.left - 8, y: y + 4, 'text-anchor': 'end', 'font-size': 11, fill: '#64748b' }, `$${Math.round(v).toLocaleString()}`));
	}

	// X labels (thin out to ~8 max).
	const step = Math.ceil(cats.length / 8) || 1;
	cats.forEach((c, i) => {
		if (i % step !== 0 && i !== cats.length - 1) return;
		const x = type === 'column' ? xBand(i) : xFor(i);
		svg.append(s('text', { x, y: H - PAD.bottom + 18, 'text-anchor': 'middle', 'font-size': 11, fill: '#64748b' }, c));
	});

	// Target plotlines.
	for (const p of plotLines) {
		const y = yFor(p.value);
		svg.append(s('line', { x1: PAD.left, y1: y, x2: W - PAD.right, y2: y, stroke: p.color ?? '#10b981', 'stroke-width': 1, 'stroke-dasharray': '6 4' }));
		if (p.label?.text) svg.append(s('text', { x: W - PAD.right, y: y - 4, 'text-anchor': 'end', 'font-size': 11, fill: p.color ?? '#10b981' }, p.label.text));
	}

	// Series.
	series.forEach((sr, si) => {
		const color = sr.color ?? ['#2563eb', '#94a3b8', '#f59e0b', '#7c3aed'][si % 4];
		if (type === 'column') {
			const bw = (plotW / Math.max(1, cats.length)) * 0.6;
			sr.data.forEach((v, i) => {
				if (v == null) return;
				const x = xBand(i) - bw / 2;
				const y = yFor(v);
				svg.append(s('rect', { x, y, width: bw, height: yFor(yMin) - y, fill: color, rx: 2 }));
			});
		} else {
			// Line: moveto to start each segment, lineto within it. penUp tracks
			// segment starts (initial point and after a null gap) so the rest are
			// linetos — otherwise an all-moveto path draws nothing.
			let d = '';
			let penUp = true;
			sr.data.forEach((v, i) => {
				if (v == null) { penUp = true; return; }
				d += `${penUp ? 'M' : 'L'}${xFor(i).toFixed(1)},${yFor(v).toFixed(1)} `;
				penUp = false;
			});
			const path = s('path', { d: d.trim(), fill: 'none', stroke: color, 'stroke-width': 2 });
			const da = dashArray(sr.dashStyle);
			if (da) path.setAttribute('stroke-dasharray', da);
			svg.append(path);
		}
	});

	const seriesColor = (si: number) => series[si].color ?? ['#2563eb', '#94a3b8', '#f59e0b', '#7c3aed'][si % 4];

	const legend = h(
		'div',
		{ class: 'legend' },
		...series.map((sr, si) =>
			h('span', { class: 'legend-item' }, h('span', { class: 'legend-swatch', style: { background: seriesColor(si) } }), sr.name)
		)
	);

	// --- hover tooltip ---
	// A vertical guide + a fixed-position tooltip (CSSOM-positioned, CSP-safe). A
	// transparent overlay over the plot area captures the pointer; we snap to the
	// nearest category and show each series' value there.
	const xAt = (i: number) => (type === 'column' ? xBand(i) : xFor(i));
	const guide = s('line', { y1: PAD.top, y2: H - PAD.bottom, stroke: '#94a3b8', 'stroke-width': 1, 'stroke-dasharray': '3 3' });
	guide.style.display = 'none';
	const overlay = s('rect', { x: PAD.left, y: PAD.top, width: plotW, height: plotH, fill: 'transparent' });
	svg.append(guide, overlay);

	const tip = h('div', { class: 'chart-tooltip' });
	tip.style.display = 'none';

	overlay.addEventListener('mousemove', (e) => {
		const r = overlay.getBoundingClientRect();
		if (r.width === 0 || cats.length === 0) return;
		const svgX = ((e.clientX - r.left) / r.width) * plotW + PAD.left;
		let idx = 0;
		let best = Infinity;
		for (let i = 0; i < cats.length; i++) {
			const dx = Math.abs(xAt(i) - svgX);
			if (dx < best) { best = dx; idx = i; }
		}
		const gx = xAt(idx);
		guide.setAttribute('x1', String(gx));
		guide.setAttribute('x2', String(gx));
		guide.style.display = '';
		tip.replaceChildren(
			h('div', { class: 'tt-title' }, cats[idx] ?? ''),
			...series
				.map((sr, si) => {
					const v = sr.data[idx];
					if (v == null) return null;
					return h(
						'div',
						{ class: 'tt-row' },
						h('span', { class: 'tt-swatch', style: { background: seriesColor(si) } }),
						`${sr.name}: ${Math.round(v).toLocaleString()}`
					);
				})
				.filter(Boolean) as ReturnType<typeof h>[]
		);
		tip.style.setProperty('left', `${e.clientX + 12}px`);
		tip.style.setProperty('top', `${e.clientY + 12}px`);
		tip.style.display = '';
	});
	overlay.addEventListener('mouseleave', () => {
		guide.style.display = 'none';
		tip.style.display = 'none';
	});

	return h(
		'div',
		{ class: 'chart-card' },
		spec.title?.text ? h('div', { class: 'chart-title' }, spec.title.text) : null,
		svg,
		series.length > 1 ? legend : null,
		tip
	);
}
